import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { 
  Search, Globe, Link2, FileText, AlertTriangle, CheckCircle, 
  TrendingUp, MapPin, Zap, RefreshCw, ExternalLink, ChevronDown,
  BarChart3, Target, Settings, FileSearch, Wand2
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const SEO = () => {
  const [dashboard, setDashboard] = useState(null);
  const [issues, setIssues] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [backlinks, setBacklinks] = useState(null);
  const [localSeo, setLocalSeo] = useState(null);
  const [technical, setTechnical] = useState(null);
  const [contentSuggestions, setContentSuggestions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [auditRunning, setAuditRunning] = useState(false);
  const [autoFixRunning, setAutoFixRunning] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [dashboardRes, issuesRes, keywordsRes, backlinksRes, localRes, techRes, contentRes] = await Promise.all([
        axios.get(`${API}/seo/dashboard`),
        axios.get(`${API}/seo/issues?limit=20`),
        axios.get(`${API}/seo/keywords`),
        axios.get(`${API}/seo/backlinks`),
        axios.get(`${API}/seo/local-seo`),
        axios.get(`${API}/seo/technical`),
        axios.get(`${API}/seo/content/suggestions`)
      ]);
      
      setDashboard(dashboardRes.data);
      setIssues(issuesRes.data.issues || []);
      setKeywords(keywordsRes.data.keywords || []);
      setBacklinks(backlinksRes.data);
      setLocalSeo(localRes.data);
      setTechnical(techRes.data);
      setContentSuggestions(contentRes.data);
    } catch (error) {
      console.error('Error loading SEO data:', error);
      toast.error('Fehler beim Laden der SEO-Daten');
    }
    setLoading(false);
  };

  const runAudit = async (fullSite = false) => {
    setAuditRunning(true);
    try {
      const response = await axios.post(`${API}/seo/audit`, { full_site: fullSite });
      toast.success(response.data.message);
      
      // Poll for completion
      setTimeout(() => {
        loadAllData();
        setAuditRunning(false);
      }, 10000);
    } catch (error) {
      toast.error('Fehler beim Starten des Audits');
      setAuditRunning(false);
    }
  };

  const runAutoFix = async (dryRun = false) => {
    setAutoFixRunning(true);
    try {
      const response = await axios.post(`${API}/seo/auto-fix`, { dry_run: dryRun });
      
      if (dryRun) {
        const data = response.data;
        if (data.fixable_issues === 0) {
          toast.info('Keine automatisch behebbaren Issues gefunden');
        } else {
          toast.success(`${data.fixable_issues} Issues können automatisch behoben werden`);
        }
        setAutoFixRunning(false);
      } else {
        toast.success(`Auto-Fix gestartet: ${response.data.total_issues} Issues werden behoben...`);
        
        // Poll for completion
        const pollStatus = async (fixId) => {
          try {
            const statusRes = await axios.get(`${API}/seo/auto-fix/status/${fixId}`);
            if (statusRes.data.status === 'completed') {
              const results = statusRes.data.results || {};
              toast.success(`Auto-Fix abgeschlossen: ${results.fixed || 0} behoben, ${results.failed || 0} fehlgeschlagen`);
              loadAllData();
              setAutoFixRunning(false);
            } else if (statusRes.data.status === 'failed') {
              toast.error('Auto-Fix fehlgeschlagen: ' + (statusRes.data.error || 'Unbekannter Fehler'));
              setAutoFixRunning(false);
            } else {
              setTimeout(() => pollStatus(fixId), 3000);
            }
          } catch (e) {
            setTimeout(() => pollStatus(fixId), 3000);
          }
        };
        
        pollStatus(response.data.fix_id);
      }
    } catch (error) {
      toast.error('Fehler beim Auto-Fix: ' + (error.response?.data?.detail || error.message));
      setAutoFixRunning(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'bg-green-100 border-green-200';
    if (score >= 60) return 'bg-yellow-100 border-yellow-200';
    return 'bg-red-100 border-red-200';
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      kritisch: 'bg-red-100 text-red-800',
      critical: 'bg-red-100 text-red-800',
      hoch: 'bg-orange-100 text-orange-800',
      high: 'bg-orange-100 text-orange-800',
      mittel: 'bg-yellow-100 text-yellow-800',
      medium: 'bg-yellow-100 text-yellow-800',
      niedrig: 'bg-blue-100 text-blue-800',
      low: 'bg-blue-100 text-blue-800'
    };
    return colors[priority?.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="seo-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2 flex items-center gap-3">
            <Search className="w-8 h-8 text-green-600" />
            SEO-Details
          </h1>
          <p className="text-slate-600">Detaillierte SEO-Analyse und Auto-Fix</p>
        </div>
        <div className="flex gap-3">
          <Button 
            onClick={() => runAutoFix(false)} 
            disabled={autoFixRunning || auditRunning}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <Wand2 className={`w-4 h-4 mr-2 ${autoFixRunning ? 'animate-spin' : ''}`} />
            {autoFixRunning ? 'Fixing...' : 'Auto-Fix Alle'}
          </Button>
          <a 
            href="/optimizer"
            className="inline-flex items-center px-4 py-2 bg-slate-900 text-white rounded-md hover:bg-slate-800"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Neue Analyse starten
          </a>
        </div>
      </div>

      {/* Score Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card className={`border-2 ${getScoreBg(dashboard?.overall_score || 0)}`}>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className={`text-4xl font-bold ${getScoreColor(dashboard?.overall_score || 0)}`}>
                {dashboard?.overall_score || 0}
              </div>
              <div className="text-sm text-slate-600 mt-1">SEO Score</div>
              <div className="text-xs text-slate-500 mt-1">{dashboard?.score_label}</div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-4xl font-bold text-red-600">
                {dashboard?.issues_summary?.by_type?.critical || 0}
              </div>
              <div className="text-sm text-slate-600 mt-1">Kritische Issues</div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-4xl font-bold text-yellow-600">
                {dashboard?.issues_summary?.by_type?.warning || 0}
              </div>
              <div className="text-sm text-slate-600 mt-1">Warnungen</div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600">
                {keywords?.length || 0}
              </div>
              <div className="text-sm text-slate-600 mt-1">Keywords getrackt</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs defaultValue="overview" value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-6 mb-6">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Übersicht
          </TabsTrigger>
          <TabsTrigger value="onpage" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            On-Page
          </TabsTrigger>
          <TabsTrigger value="offpage" className="flex items-center gap-2">
            <Link2 className="w-4 h-4" />
            Off-Page
          </TabsTrigger>
          <TabsTrigger value="local" className="flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            Local SEO
          </TabsTrigger>
          <TabsTrigger value="keywords" className="flex items-center gap-2">
            <Target className="w-4 h-4" />
            Keywords
          </TabsTrigger>
          <TabsTrigger value="technical" className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Technisch
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Recommendations */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-yellow-500" />
                  Top Empfehlungen
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboard?.recommendations?.map((rec, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getPriorityBadge(rec.priority)}`}>
                        {rec.priority}
                      </span>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-700">{rec.action}</p>
                        {rec.url && (
                          <p className="text-xs text-slate-500 mt-1">{rec.url}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Quick Wins */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-500" />
                  Quick Wins
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {contentSuggestions?.quick_wins?.map((win, idx) => (
                    <div key={idx} className="p-3 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-slate-700">{win.action}</span>
                        <div className="flex gap-2">
                          <Badge variant="outline" className="text-xs">
                            Impact: {win.impact}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            Aufwand: {win.effort}
                          </Badge>
                        </div>
                      </div>
                      <p className="text-sm text-slate-600">{win.details}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Recent Issues */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-orange-500" />
                  Aktuelle SEO-Issues
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {issues.slice(0, 8).map((issue, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          issue.type === 'critical' ? 'bg-red-100 text-red-800' :
                          issue.type === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {issue.type}
                        </span>
                        <div>
                          <p className="text-sm font-medium text-slate-700">{issue.issue}</p>
                          <p className="text-xs text-slate-500">{issue.url}</p>
                        </div>
                      </div>
                      <Badge variant="outline">Impact: {issue.impact}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* On-Page Tab */}
        <TabsContent value="onpage">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Issues by Category */}
            <Card>
              <CardHeader>
                <CardTitle>Issues nach Kategorie</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(dashboard?.issues_summary?.by_type || {}).map(([type, count]) => (
                    <div key={type} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="font-medium capitalize">{type}</span>
                      <Badge variant={type === 'critical' ? 'destructive' : 'secondary'}>
                        {count}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Content Suggestions */}
            <Card>
              <CardHeader>
                <CardTitle>Content-Optimierung</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {contentSuggestions?.page_suggestions?.map((page, idx) => (
                    <div key={idx} className="p-3 border rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline">{page.type}</Badge>
                        <span className="text-sm font-medium">{page.url}</span>
                      </div>
                      <ul className="space-y-1">
                        {page.suggestions.map((sug, sidx) => (
                          <li key={sidx} className="text-sm text-slate-600 flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                            {sug}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Content Gaps */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Content-Lücken & Chancen</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {contentSuggestions?.content_gaps?.map((gap, idx) => (
                    <div key={idx} className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <h4 className="font-semibold text-green-800 mb-2">{gap.topic}</h4>
                      <p className="text-sm text-green-700 mb-3">{gap.recommendation}</p>
                      <div className="flex flex-wrap gap-2">
                        {gap.potential_keywords?.map((kw, kidx) => (
                          <Badge key={kidx} variant="outline" className="bg-white">
                            {kw}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Off-Page Tab */}
        <TabsContent value="offpage">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Backlink Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Link2 className="w-5 h-5" />
                  Backlink-Übersicht
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="text-center p-4 bg-slate-50 rounded-lg">
                    <div className="text-2xl font-bold text-slate-700">
                      {backlinks?.summary?.total_backlinks || 0}
                    </div>
                    <div className="text-sm text-slate-500">Total Backlinks</div>
                  </div>
                  <div className="text-center p-4 bg-slate-50 rounded-lg">
                    <div className="text-2xl font-bold text-slate-700">
                      {backlinks?.summary?.unique_domains || 0}
                    </div>
                    <div className="text-sm text-slate-500">Unique Domains</div>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">
                      {backlinks?.summary?.dofollow || 0}
                    </div>
                    <div className="text-sm text-green-700">DoFollow</div>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-gray-600">
                      {backlinks?.summary?.nofollow || 0}
                    </div>
                    <div className="text-sm text-gray-500">NoFollow</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Link Building Recommendations */}
            <Card>
              <CardHeader>
                <CardTitle>Linkaufbau-Empfehlungen</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {backlinks?.recommendations?.map((rec, idx) => (
                    <div key={idx} className="p-3 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">{rec.action}</span>
                        <Badge className={getPriorityBadge(rec.priority)}>
                          {rec.priority}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-600">{rec.description}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Link Opportunities */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Link-Opportunities</CardTitle>
                <CardDescription>Potenzielle Backlink-Quellen basierend auf Branche und Region</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {backlinks?.opportunities?.map((opp, idx) => (
                    <div key={idx}>
                      <h4 className="font-semibold text-slate-700 mb-3">{opp.name}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                        {opp.potential_links?.map((link, lidx) => (
                          <div key={lidx} className="p-3 bg-slate-50 rounded-lg flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium">{link.domain}</p>
                              <p className="text-xs text-slate-500">DA: {link.da}</p>
                            </div>
                            <Badge variant={link.status === 'empfohlen' ? 'default' : 'outline'}>
                              {link.status}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Local SEO Tab */}
        <TabsContent value="local">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Business Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-red-500" />
                  Geschäftsinformationen
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="text-sm text-slate-500">Firmenname</div>
                    <div className="font-medium">{localSeo?.business_info?.name}</div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="text-sm text-slate-500">Adresse</div>
                    <div className="font-medium">{localSeo?.business_info?.address}</div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="text-sm text-slate-500">Telefon</div>
                    <div className="font-medium">{localSeo?.business_info?.phone}</div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="text-sm text-slate-500">Website</div>
                    <div className="font-medium">{localSeo?.business_info?.website}</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Local SEO Checklist */}
            <Card>
              <CardHeader>
                <CardTitle>Local SEO Checkliste</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {localSeo?.checklist?.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 border rounded-lg">
                      <span className="text-sm">{item.item}</span>
                      <div className="flex items-center gap-2">
                        <Badge className={getPriorityBadge(item.priority)}>
                          {item.priority}
                        </Badge>
                        <Badge variant="outline">{item.status}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Local Keywords */}
            <Card>
              <CardHeader>
                <CardTitle>Lokale Keywords</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {localSeo?.local_rankings?.keywords?.map((kw, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="text-sm font-medium">{kw.keyword}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500">Ziel: Top {kw.target}</span>
                        <Badge variant="outline">{kw.position}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Schema Markup */}
            <Card>
              <CardHeader>
                <CardTitle>LocalBusiness Schema</CardTitle>
                <CardDescription>JSON-LD Markup für Rich Snippets</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="bg-slate-900 text-slate-100 rounded-lg p-4 overflow-x-auto">
                  <pre className="text-xs font-mono whitespace-pre-wrap">
                    {localSeo?.schema_markup?.recommended_json_ld}
                  </pre>
                </div>
                <Button 
                  variant="outline" 
                  className="mt-4 w-full"
                  onClick={() => {
                    navigator.clipboard.writeText(localSeo?.schema_markup?.recommended_json_ld || '');
                    toast.success('Schema-Markup kopiert!');
                  }}
                >
                  Schema kopieren
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Keywords Tab */}
        <TabsContent value="keywords">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-blue-500" />
                Keyword-Tracking
              </CardTitle>
              <CardDescription>
                Wichtige Keywords für Druckerei in Zürich
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3 font-medium text-slate-600">Keyword</th>
                      <th className="text-left p-3 font-medium text-slate-600">Ziel-Position</th>
                      <th className="text-left p-3 font-medium text-slate-600">Aktuelle Position</th>
                      <th className="text-left p-3 font-medium text-slate-600">Priorität</th>
                      <th className="text-left p-3 font-medium text-slate-600">Ziel-URL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {keywords.map((kw, idx) => (
                      <tr key={idx} className="border-b hover:bg-slate-50">
                        <td className="p-3 font-medium">{kw.keyword}</td>
                        <td className="p-3">
                          <Badge variant="outline">Top {kw.target_position}</Badge>
                        </td>
                        <td className="p-3">
                          {kw.current_position ? (
                            <Badge className={kw.current_position <= kw.target_position ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                              #{kw.current_position}
                            </Badge>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="p-3">
                          <div className="flex">
                            {[...Array(kw.priority)].map((_, i) => (
                              <div key={i} className="w-2 h-2 bg-green-500 rounded-full mr-0.5"></div>
                            ))}
                          </div>
                        </td>
                        <td className="p-3 text-sm text-slate-500">{kw.url}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Tipp:</strong> Verbinden Sie Google Search Console für Live-Ranking-Daten 
                  oder nutzen Sie Tools wie Ahrefs, SEMrush oder Sistrix für detailliertes Tracking.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Technical Tab */}
        <TabsContent value="technical">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Robots & Sitemap */}
            <Card>
              <CardHeader>
                <CardTitle>Robots.txt & Sitemap</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">robots.txt</span>
                      <Badge variant="outline">{technical?.robots_txt?.status}</Badge>
                    </div>
                    <a 
                      href={technical?.robots_txt?.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                    >
                      {technical?.robots_txt?.url}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">sitemap.xml</span>
                      <Badge variant="outline">{technical?.sitemap?.status}</Badge>
                    </div>
                    <a 
                      href={technical?.sitemap?.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                    >
                      {technical?.sitemap?.url}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Core Web Vitals */}
            <Card>
              <CardHeader>
                <CardTitle>Core Web Vitals</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(technical?.core_web_vitals || {}).map(([key, vital]) => (
                    <div key={key} className="p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-sm">{vital.name}</span>
                        <Badge variant="outline">{vital.status}</Badge>
                      </div>
                      <div className="text-xs text-slate-500">Ziel: {vital.target}</div>
                    </div>
                  ))}
                </div>
                <a 
                  href="https://pagespeed.web.dev/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center gap-2 text-sm text-blue-600 hover:underline"
                >
                  PageSpeed Insights öffnen
                  <ExternalLink className="w-3 h-3" />
                </a>
              </CardContent>
            </Card>

            {/* Page Speed Recommendations */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Performance-Empfehlungen</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {technical?.page_speed?.recommendations?.map((rec, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                      <Zap className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{rec}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SEO;
